import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import {
  Button,
  Drawer,
  Form,
  Grid,
  Input,
  Modal,
  Select,
  Space,
  TreeSelect,
  Typography,
} from 'antd';

import { conns } from '@cfg/axiosConfig.tsx';
import {
  expandDashboardCategoryValues,
  normalizeDashboardCategoryTree,
  type DashboardCategoryTreeNode,
} from '@eshop/utils/dashboardCategoryTree';

const { Text } = Typography;

type CategoryPopulationScope = 'items' | 'appointment_services';
type PrimitiveValue = string | number | boolean;

export interface ConfigurableSearchSelectFilter<TValue extends PrimitiveValue = PrimitiveValue> {
  key: string;
  label: string;
  placeholder?: string;
  value?: TValue;
  options: Array<{ value: TValue; label: ReactNode }>;
  allowClear?: boolean;
  showSearch?: boolean;
  minWidth?: number;
  onChange: (value: TValue | undefined) => void;
}

export interface ConfigurableSearchTextFilter {
  key: string;
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}

export interface ConfigurableItemSearchModalProps {
  open: boolean;
  title: string;
  query: string;
  queryLabel?: string;
  queryPlaceholder?: string;
  selectedCategoryValues?: unknown[];
  categoryLabel?: string;
  categoryPlaceholder?: string;
  categoryPopulationScope: CategoryPopulationScope;
  selectFilters?: ConfigurableSearchSelectFilter[];
  textFilters?: ConfigurableSearchTextFilter[];
  selectedSummary?: ReactNode;
  resetDisabled?: boolean;
  applyText?: string;
  cancelText?: string;
  width?: number;
  children?: ReactNode;
  onQueryChange: (value: string) => void;
  onCategoryValuesChange?: (values: unknown[], expandedIds: number[]) => void;
  onApply: () => void;
  onReset?: () => void;
  onClose: () => void;
}

const toNumericIds = (values: unknown[]): number[] =>
  values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .map((value) => Math.trunc(value));

export function ConfigurableItemSearchModal({
  open,
  title,
  query,
  queryLabel = 'Buscar',
  queryPlaceholder = 'Nombre, SKU o descripción',
  selectedCategoryValues = [],
  categoryLabel = 'Categorías',
  categoryPlaceholder = 'Todas',
  categoryPopulationScope,
  selectFilters = [],
  textFilters = [],
  selectedSummary,
  resetDisabled = false,
  applyText = 'Aplicar',
  cancelText = 'Cancelar',
  width = 720,
  children,
  onQueryChange,
  onCategoryValuesChange,
  onApply,
  onReset,
  onClose,
}: ConfigurableItemSearchModalProps): JSX.Element {
  const screens = Grid.useBreakpoint();
  const isMobile = Boolean(screens.xs) && !screens.md;
  const [categoryTree, setCategoryTree] = useState<DashboardCategoryTreeNode[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);

  useEffect(() => {
    if (!open || !onCategoryValuesChange) {
      return;
    }

    let active = true;
    const loadCategories = async () => {
      setCategoryLoading(true);
      try {
        const { data } = await conns<unknown[]>('eshop:dashboard:category:tree').get({
          populated_only: true,
          population_scope: categoryPopulationScope,
        });
        if (active) {
          setCategoryTree(normalizeDashboardCategoryTree(data, { allowMergedSelection: true }));
        }
      } catch (error) {
        console.error('No se pudo cargar el árbol de categorías', error);
        if (active) {
          setCategoryTree([]);
        }
      } finally {
        if (active) {
          setCategoryLoading(false);
        }
      }
    };

    void loadCategories();

    return () => {
      active = false;
    };
  }, [categoryPopulationScope, onCategoryValuesChange, open]);

  const handleCategoryChange = useCallback(
    (value: unknown) => {
      const rawValues = Array.isArray(value) ? value : value == null ? [] : [value];
      const expanded = expandDashboardCategoryValues(rawValues, categoryTree);
      onCategoryValuesChange?.(rawValues, toNumericIds(expanded));
    },
    [categoryTree, onCategoryValuesChange],
  );

  const categoryValue = useMemo(
    () => (Array.isArray(selectedCategoryValues) ? selectedCategoryValues : []),
    [selectedCategoryValues],
  );

  const body = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Form layout="vertical">
        <Form.Item label={queryLabel} htmlFor="configurableItemSearchQuery">
          <Input.Search
            id="configurableItemSearchQuery"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onSearch={onQueryChange}
            allowClear
            placeholder={queryPlaceholder}
          />
        </Form.Item>

        {onCategoryValuesChange || selectFilters.length > 0 || textFilters.length > 0 ? (
          <Space
            wrap={!isMobile}
            direction={isMobile ? 'vertical' : 'horizontal'}
            align="start"
            style={{ width: '100%' }}
          >
            {onCategoryValuesChange ? (
              <Form.Item
                label={categoryLabel}
                style={{ minWidth: 240, width: isMobile ? '100%' : 280 }}
              >
                <TreeSelect
                  treeData={categoryTree}
                  value={categoryValue}
                  loading={categoryLoading}
                  allowClear
                  treeCheckable
                  showSearch
                  treeNodeFilterProp="label"
                  showCheckedStrategy={TreeSelect.SHOW_PARENT}
                  placeholder={categoryPlaceholder}
                  onChange={handleCategoryChange}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            ) : null}

            {selectFilters.map((filter) => (
              <Form.Item
                key={filter.key}
                label={filter.label}
                style={{ minWidth: filter.minWidth ?? 180, width: isMobile ? '100%' : undefined }}
              >
                <Select
                  value={filter.value}
                  options={filter.options}
                  placeholder={filter.placeholder}
                  allowClear={filter.allowClear ?? true}
                  showSearch={filter.showSearch ?? true}
                  optionFilterProp="label"
                  onChange={(value) => filter.onChange(value as PrimitiveValue | undefined)}
                />
              </Form.Item>
            ))}

            {textFilters.map((filter) => (
              <Form.Item
                key={filter.key}
                label={filter.label}
                style={{ minWidth: 180, width: isMobile ? '100%' : undefined }}
              >
                <Input
                  value={filter.value}
                  placeholder={filter.placeholder}
                  onChange={(event) => filter.onChange(event.target.value)}
                  allowClear
                />
              </Form.Item>
            ))}
          </Space>
        ) : null}
      </Form>

      {selectedSummary ? <Text type="secondary">{selectedSummary}</Text> : null}
      {children}
    </Space>
  );

  const footer = (
    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
      {onReset ? (
        <Button onClick={onReset} disabled={resetDisabled}>
          Limpiar
        </Button>
      ) : null}
      <Button onClick={onClose}>{cancelText}</Button>
      <Button type="primary" onClick={onApply}>
        {applyText}
      </Button>
    </Space>
  );

  if (isMobile) {
    return (
      <Drawer
        title={title}
        open={open}
        onClose={onClose}
        placement="bottom"
        height="88vh"
        destroyOnHidden={false}
        maskClosable={false}
        footer={footer}
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      onOk={onApply}
      okText={applyText}
      cancelText={cancelText}
      width={width}
      destroyOnHidden={false}
      maskClosable={false}
      footer={footer}
    >
      {body}
    </Modal>
  );
}

export default ConfigurableItemSearchModal;
